import React from 'react';
import PropTypes from 'prop-types';
import { LoadingButton } from 'client/shared/Spinner/LoadingButton';
import { handleGetCsrfToken, useCsrfToken } from 'client/hooks/useCsrfToken';
import { useT } from 'client/i18n/hooks/useT';
import styles from './SupportersCancelScreen.sass';

export const SupportersCancelScreen = ({
  podcastName,
  podcastUrl,
  cancelCode,
  isPageLoading,
  podcastStationId,
  onClickCancel,
  isCancelComplete,
  isCancellationProcessing,
}) => {
  const t = useT();
  const { csrfToken } = useCsrfToken();

  const handleClickCancelButton = async () => {
    const _csrf = csrfToken || (await handleGetCsrfToken());
    onClickCancel(podcastStationId, cancelCode, _csrf);
  };

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        {isPageLoading ? (
          <div className={styles.loading}>
            {t(
              'SupportersCancelScreen.Loading',
              'Loading...',
              'An alert telling a user the supporters cancel screen is currently loading'
            )}
          </div>
        ) : (
          <>
            <div className={styles.title}>
              {t(
                'SupportersCancelScreen.Title',
                'Stop supporting {PODCAST_NAME}',
                'The title for the supporters cancel screen letting the user know they are about to stop supporting a specific podcast',
                {
                  PODCAST_NAME: (
                    <span
                      className={styles.podcastTitle}
                      key={`supporters-cancel-screen-title__${podcastName}`}
                    >
                      {podcastName}
                    </span>
                  ),
                }
              )}
            </div>
            {isCancelComplete ? (
              <div className={styles.bodyText}>
                {t(
                  'SupportersCancelScreen.Message.Complete',
                  "We've canceled your subscription. If you ever want to support this podcast again in the future, you can always sign up again from their profile at {PODCAST_URL}",
                  'The message for the supporters cancel screen letting the user know they have successfully stopped supporting a specific podcast and can resign up visiting the url for that podcast. The PODCAST_URL variable is the full url for the podcast',
                  {
                    PODCAST_URL: (
                      <span
                        className={styles.podcastUrl}
                        key={`supporters-cancel-screen-completed-message__${podcastUrl}`}
                      >
                        {podcastUrl}
                      </span>
                    ),
                  }
                )}
              </div>
            ) : (
              <>
                <div className={styles.bodyText}>
                  {t(
                    'SupportersCancelScreen.Message.Uncomplete',
                    "If you'd like to stop supporting this podcast, just click Cancel. You won't be charged again going forward.",
                    'The message for the supporters cancel screen letting the user know how to stop supporting a specific podcast'
                  )}
                </div>
                <LoadingButton
                  type="submit"
                  color="purple"
                  isLoading={isCancellationProcessing}
                  bsClass={styles.cancelButton}
                  onClick={handleClickCancelButton}
                >
                  {t(
                    'SupportersCancelScreen.Message.CancelAction',
                    'Cancel my subscription',
                    'The action a user must take to cancel subcription to a specific podcast for the supporters cancel screen'
                  )}
                </LoadingButton>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

SupportersCancelScreen.propTypes = {
  podcastName: PropTypes.string.isRequired,
  podcastUrl: PropTypes.string.isRequired,
  cancelCode: PropTypes.string.isRequired,
  podcastStationId: PropTypes.string.isRequired,
  onClickCancel: PropTypes.func.isRequired,
  isCancelComplete: PropTypes.bool.isRequired,
  isCancellationProcessing: PropTypes.bool.isRequired,
  isPageLoading: PropTypes.bool.isRequired,
};
